import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Download, Mail, RefreshCw, Home, Loader2, Lock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchRef, setSearchRef] = useState("");
  const [resendEmail, setResendEmail] = useState({});
  const [sendingEmail, setSendingEmail] = useState({});

  // Check if already authenticated (session storage)
  useEffect(() => {
    const isAuth = sessionStorage.getItem('adminAuth');
    if (isAuth === 'true') {
      setAuthenticated(true);
    }
  }, []);

  const handleLogin = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/verify`, { password });
      setAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      toast.success("Welcome, Admin!");
      fetchCVs();
    } catch (error) {
      toast.error("Invalid password");
      setPassword("");
    } finally {
      setVerifying(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setAuthenticated(false);
    setCvs([]);
  };

  const fetchCVs = async (email = "", reference = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (email) params.append("email", email);
      if (reference) params.append("reference", reference);
      
      const response = await axios.get(`${API}/admin/cvs?${params.toString()}`);
      setCvs(response.data.cvs || []);
    } catch (error) {
      toast.error("Failed to fetch CVs");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) {
      fetchCVs();
    }
  }, [authenticated]);

  const handleSearch = () => {
    fetchCVs(searchEmail, searchRef);
  };

  const handleClearSearch = () => {
    setSearchEmail("");
    setSearchRef("");
    fetchCVs();
  };

  const handleDownload = (reference) => {
    window.open(`${API}/cv/download/${reference}`, '_blank');
  };

  const handleResendCV = async (reference) => {
    const email = resendEmail[reference];
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSendingEmail(prev => ({ ...prev, [reference]: true }));
    try {
      await axios.post(`${API}/admin/resend-cv?reference=${reference}&recipient_email=${encodeURIComponent(email)}`);
      toast.success(`CV resent to ${email}`);
      setResendEmail(prev => ({ ...prev, [reference]: "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend CV");
    } finally {
      setSendingEmail(prev => ({ ...prev, [reference]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bg-card border border-border p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h1 className="font-heading text-2xl font-semibold">Admin Access</h1>
            <p className="text-muted-foreground text-sm mt-1">Enter password to continue</p>
          </div>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              className="rounded-none"
              data-testid="admin-password"
            />
            <Button
              onClick={handleLogin}
              disabled={verifying || !password}
              className="w-full rounded-none bg-accent"
              data-testid="admin-login-btn"
            >
              {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {verifying ? "Verifying..." : "Access Admin"}
            </Button>
          </div>
          <div className="mt-6 text-center">
            <Button
              onClick={() => window.location.href = '/'}
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
            >
              <Home className="h-4 w-4 mr-1" />
              Back to Site
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold">CV Admin</h1>
            <p className="text-muted-foreground">Manage and resend customer CVs</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              className="rounded-none"
            >
              <Home className="mr-2 h-4 w-4" />
              Back to Site
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-none text-destructive hover:bg-destructive/10"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-card border border-border p-6 mb-6">
          <h2 className="font-medium mb-4">Search CVs</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Search by email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="flex-1 rounded-none"
            />
            <Input
              placeholder="Search by reference..."
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value)}
              className="flex-1 rounded-none"
            />
            <Button onClick={handleSearch} className="rounded-none bg-accent">
              <Search className="mr-2 h-4 w-4" />
              Search
            </Button>
            <Button onClick={handleClearSearch} variant="outline" className="rounded-none">
              <RefreshCw className="mr-2 h-4 w-4" />
              Clear
            </Button>
          </div>
        </div>

        {/* CV List */}
        <div className="bg-card border border-border">
          <div className="p-4 border-b border-border">
            <h2 className="font-medium">Recent CVs ({cvs.length})</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-2 text-muted-foreground">Loading CVs...</p>
            </div>
          ) : cvs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No CVs found
            </div>
          ) : (
            <div className="divide-y divide-border">
              {cvs.map((cv) => (
                <div key={cv.id} className="p-4 hover:bg-secondary/20">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* CV Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium truncate">{cv.fullName}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          cv.status === 'completed' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {cv.status}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground space-y-0.5">
                        <p><span className="font-medium">Email:</span> {cv.email}</p>
                        <p><span className="font-medium">Reference:</span> {cv.reference}</p>
                        <p><span className="font-medium">Template:</span> {cv.template} | <span className="font-medium">Amount:</span> ${cv.amount?.toFixed(2)}</p>
                        <p><span className="font-medium">Created:</span> {formatDate(cv.createdAt)}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 lg:w-80">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleDownload(cv.reference)}
                          variant="outline"
                          size="sm"
                          className="flex-1 rounded-none"
                        >
                          <Download className="mr-1 h-3 w-3" />
                          Download
                        </Button>
                        <Button
                          onClick={() => window.open(`/cv/share/${cv.shareToken}`, '_blank')}
                          variant="outline"
                          size="sm"
                          className="flex-1 rounded-none"
                        >
                          View
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Email to resend..."
                          value={resendEmail[cv.reference] || ""}
                          onChange={(e) => setResendEmail(prev => ({ 
                            ...prev, 
                            [cv.reference]: e.target.value 
                          }))}
                          className="flex-1 rounded-none h-9 text-sm"
                        />
                        <Button
                          onClick={() => handleResendCV(cv.reference)}
                          disabled={sendingEmail[cv.reference]}
                          size="sm"
                          className="rounded-none bg-accent"
                        >
                          {sendingEmail[cv.reference] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Mail className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
